import { useState, useMemo, useCallback } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  format,
  getDate
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { getHoursForDay } from '@/lib/heures-utils';
import { usePlanningStore } from '@/stores/planningStore';
import type { CalendarAffectation, CalendarDay, DayPlanning } from '@/types/calendar';

export type ViewMode = 'semaine' | 'mois';

interface UseCalendarProps {
  affectations: CalendarAffectation[];
}

export function useCalendar({ affectations }: UseCalendarProps) {
  // Récupérer les filtres depuis le store
  const filters = usePlanningStore((state) => state.filters);

  // Filtrer les affectations selon les critères
  const filteredAffectations = useMemo(() => {
    return affectations.filter((aff) => {
      // Filtre par chantier
      if (filters.chantierIds.length > 0) {
        if (!filters.chantierIds.includes(aff.chantier_id)) {
          return false;
        }
      }

      // Filtre par équipe
      if (filters.equipeIds.length > 0) {
        if (!aff.equipe_id || !filters.equipeIds.includes(aff.equipe_id)) {
          return false;
        }
      }

      // Filtre par recherche texte
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase().trim();
        const nomChantier = aff.chantier?.nom_chantier?.toLowerCase() || '';
        const nomMembre = aff.membre?.nom?.toLowerCase() || '';
        const prenomMembre = aff.membre?.prenom?.toLowerCase() || '';
        const nomComplet = `${prenomMembre} ${nomMembre}`;

        if (!nomChantier.includes(query) && !nomMembre.includes(query) && !prenomMembre.includes(query) && !nomComplet.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [affectations, filters.chantierIds, filters.equipeIds, filters.searchQuery]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('mois'); // Mois par défaut

  // Générer les jours du calendrier (6 semaines pour couvrir tous les mois)
  const calendarDays = useMemo((): CalendarDay[] => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    // Semaine commence le lundi (weekStartsOn: 1)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return days.map((date): CalendarDay => {
      // Filtrer les affectations actives pour ce jour (utilise les affectations déjà filtrées)
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayAffectations = filteredAffectations.filter((aff) => {
        // Normaliser les dates en chaînes YYYY-MM-DD pour éviter les problèmes de timezone
        const debutStr = aff.date_debut.split('T')[0];
        const finStr = aff.date_fin.split('T')[0];

        // Vérifier si le jour est dans la plage date_debut - date_fin
        if (dateStr < debutStr || dateStr > finStr) {
          return false;
        }

        // Si jours_details est disponible, utiliser cette info précise
        if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
          return aff.jours_details.some(jd => jd.date === dateStr);
        }

        // Fallback: inclure tous les jours dans la plage
        return true;
      });

      // Extraire les spécialités uniques pour les points colorés
      const specialites = [
        ...new Set(
          dayAffectations.map(
            (a) => a.equipe?.specialite || a.membre?.specialite || 'default'
          )
        ),
      ];

      return {
        date,
        dayOfMonth: getDate(date),
        isCurrentMonth: isSameMonth(date, currentDate),
        isToday: isToday(date),
        isSelected: isSameDay(date, selectedDate),
        affectations: dayAffectations,
        specialites,
      };
    });
  }, [currentDate, selectedDate, filteredAffectations]);

  // Planning du jour sélectionné
  const selectedDayPlanning = useMemo((): DayPlanning | null => {
    const dayData = calendarDays.find((d) => isSameDay(d.date, selectedDate));
    if (!dayData) return null;

    // Utiliser getHoursForDay pour calculer les heures réelles (demi-journée vs journée complète)
    const totalHeures = dayData.affectations.reduce(
      (sum, a) => sum + getHoursForDay(a, selectedDate),
      0
    );

    const totalCout = dayData.affectations.reduce((sum, a) => {
      const tauxHoraire = a.taux_horaire_specifique ?? a.membre?.taux_horaire ?? 0;
      const coef = a.membre?.coefficient_charges || 1.85;

      // Utiliser getHoursForDay pour les heures réelles
      const heures = getHoursForDay(a, selectedDate);
      const coutJournalier = heures * tauxHoraire * (coef || 1.85);

      return sum + coutJournalier;
    }, 0);

    const membresIds = new Set(dayData.affectations.map((a) => a.membre_equipe_id));
    const chantiersIds = new Set(dayData.affectations.map((a) => a.chantier_id));

    return {
      date: selectedDate,
      affectations: dayData.affectations,
      totalHeures,
      totalCout,
      membresCount: membresIds.size,
      chantiersCount: chantiersIds.size,
    };
  }, [calendarDays, selectedDate]);

  // Navigation (gère semaine ou mois selon le mode)
  const goToNext = useCallback(() => {
    if (viewMode === 'semaine') {
      setCurrentDate((prev) => addWeeks(prev, 1));
    } else {
      setCurrentDate((prev) => addMonths(prev, 1));
    }
  }, [viewMode]);

  const goToPrev = useCallback(() => {
    if (viewMode === 'semaine') {
      setCurrentDate((prev) => subWeeks(prev, 1));
    } else {
      setCurrentDate((prev) => subMonths(prev, 1));
    }
  }, [viewMode]);

  // Anciens noms pour compatibilité
  const goToNextMonth = goToNext;
  const goToPrevMonth = goToPrev;

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  }, []);

  const selectDate = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === 'semaine' ? 'mois' : 'semaine'));
  }, []);

  // Labels formatés
  const currentMonthLabel = format(currentDate, 'MMMM', { locale: fr });
  const currentYear = format(currentDate, 'yyyy');
  const selectedDateLabel = format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr });

  // Label du titre selon la vue
  const viewLabel = useMemo(() => {
    if (viewMode === 'semaine') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'd', { locale: fr })} - ${format(end, 'd MMM yyyy', { locale: fr })}`;
    }
    return `${currentMonthLabel} ${currentYear}`;
  }, [viewMode, currentDate, currentMonthLabel, currentYear]);

  // Jours de la semaine (pour l'en-tête)
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return {
    currentDate,
    selectedDate,
    calendarDays,
    selectedDayPlanning,
    currentMonthLabel,
    currentYear,
    selectedDateLabel,
    viewLabel,
    weekDays,
    viewMode,
    setViewMode,
    toggleViewMode,
    goToNext,
    goToPrev,
    goToNextMonth,
    goToPrevMonth,
    goToToday,
    selectDate,
    // Retourner les affectations filtrées (le calendrier et le planning les utilisent)
    affectations: filteredAffectations,
  };
}
